import { touchPrefs } from "../input/touchLayout.js";

/**
 * El movil vibra con el partido.
 *
 * Un boton de cristal no se siente: el pulgar no sabe si ha pulsado, si la
 * carga ya es tiro o si el balon le ha llegado a menos que mire. En una
 * consola eso lo cuenta el mando vibrando; aqui lo cuenta el telefono. Cada
 * cosa tiene SU golpe, y se aprenden sin que nadie los explique: el toque seco
 * al pulsar, el "clic" cuando la carga pasa de pase a tiro, el doble cuando
 * llega al punto perfecto, el golpe al soltar (mas fuerte cuanto mas fuerte
 * sale), el balon que te llega, la entrada que te lo quita, el palo, el gol.
 *
 * - **Android** (Chrome, Firefox, Samsung): `navigator.vibrate`, con patrones.
 * - **iPhone** (Safari no tiene `vibrate`): desde iOS 18 un `<input switch>`
 *   hace el "tic" del sistema al cambiar. Se pulsa uno invisible. Solo da un
 *   golpe corto y solo con el dedo en la pantalla, que es justo cuando mas
 *   sirve (pulsar, soltar). Donde no funciona, no pasa nada.
 *
 * Nunca decide nada del partido: si el movil no vibra, se juega igual.
 */
export type HapticKind =
  | "press"
  | "threshold"
  | "perfect"
  | "release"
  | "receive"
  | "tackled"
  | "bump"
  | "post"
  | "goal"
  | "conceded";

const PATTERNS: Record<HapticKind, number | number[]> = {
  press: 9,
  threshold: 14,
  perfect: [12, 45, 22],
  release: 18,
  receive: 11,
  tackled: [34, 30, 12],
  bump: 22,
  post: [40, 35, 18],
  goal: [30, 50, 30, 50, 110],
  conceded: 90,
};

/**
 * Minimo entre dos vibraciones. Sin esto, recuperar ticks en online (decenas
 * de golpes en un frame) seria un zumbido continuo.
 */
const MIN_GAP_MS = 45;
let lastAt = 0;

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

type VibrateNavigator = Navigator & { vibrate?: (pattern: number | number[]) => boolean };

function canVibrate(): boolean {
  return typeof navigator !== "undefined" && typeof (navigator as VibrateNavigator).vibrate === "function";
}

function isAppleTouch(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent ?? "";
  return /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1);
}

/**
 * El "tic" del iPhone: un interruptor (`<input switch>`, iOS 18+) que se pulsa
 * sin verse. Va en `<head>` y con `display: none` para que ni se pinte ni
 * pueda llevarse el foco del chat; el clic se corta ahi mismo para que nadie
 * mas del documento lo oiga.
 */
function appleTick(): void {
  if (typeof document === "undefined") return;
  try {
    const label = document.createElement("label");
    label.setAttribute("aria-hidden", "true");
    label.style.display = "none";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.setAttribute("switch", "");
    input.tabIndex = -1;
    label.appendChild(input);
    label.addEventListener("click", (event) => event.stopPropagation());
    document.head.appendChild(label);
    label.click();
    label.remove();
  } catch {
    /* sin vibracion */
  }
}

/**
 * Vibra. `strength` (0-1) solo cuenta en el golpe al soltar: un pase se nota
 * menos que un tiro a tope.
 */
export function haptic(kind: HapticKind, strength = 1): void {
  if (!touchPrefs().vibration) return;
  const t = now();
  if (t - lastAt < MIN_GAP_MS) return;
  lastAt = t;
  if (canVibrate()) {
    let pattern = PATTERNS[kind];
    if (kind === "release") pattern = Math.round(10 + 22 * Math.max(0, Math.min(1, strength)));
    try {
      (navigator as VibrateNavigator).vibrate!(pattern);
    } catch {
      /* sin vibracion */
    }
    return;
  }
  if (isAppleTouch()) appleTick();
}

/** ¿Hay forma de vibrar aqui? Para enseñar o no el interruptor. */
export function hapticsAvailable(): boolean {
  return canVibrate() || isAppleTouch();
}

/** Solo tests. */
export function resetHapticsForTest(): void {
  lastAt = -Infinity;
}
